/* eslint-disable @typescript-eslint/no-explicit-any */
import { OfficeAdapter } from './office-adapter';
import type { AppointmentSnapshot, OfficeAdapterEvents, OfficeThemeMode, OutlookContext, PaneInitializationContext } from './office-types';

/**
 * Production adapter over the Office.js global. Office.js ships no bundled
 * typings here (loaded from the CDN at runtime), so the touchpoints are typed
 * locally and accessed defensively — every capability degrades gracefully on
 * hosts below its requirement set (TECHNICAL-SPECIFICATION.md §3/§5).
 */
export class RealOfficeAdapter extends OfficeAdapter {
  private get office(): any {
    return (globalThis as any).Office;
  }
  private get mailbox(): any {
    return this.office?.context?.mailbox;
  }
  private get item(): any {
    return this.mailbox?.item;
  }

  override context(): OutlookContext {
    const item = this.item;
    if (!item) return 'mail'; // pinned pane / no-item context (Mailbox 1.13)
    const types = this.office?.MailboxEnums?.ItemType;
    if (item.itemType === types?.Appointment || item.itemType === 'appointment') {
      // Compose items expose setAsync on start; read items do not.
      return typeof item.start?.setAsync === 'function' ? 'compose' : 'read';
    }
    return 'mail';
  }

  override canWriteItem(): boolean {
    return this.context() === 'compose';
  }

  override userEmail(): string | null {
    return this.mailbox?.userProfile?.emailAddress ?? null;
  }

  override async readAppointment(): Promise<AppointmentSnapshot | null> {
    const item = this.item;
    if (!item || this.context() === 'mail') return null;

    if (this.context() === 'read') {
      const locations = await this.getEnhancedLocations();
      return {
        subject: item.subject ?? '',
        start: item.start instanceof Date ? item.start : new Date(item.start),
        end: item.end instanceof Date ? item.end : new Date(item.end),
        roomEmails: locations,
        requiredAttendees: (item.requiredAttendees ?? []).map(mapRecipient),
        optionalAttendees: (item.optionalAttendees ?? []).map(mapRecipient),
        organizer: item.organizer ? mapRecipient(item.organizer) : null,
        iCalUId: null,
      };
    }

    const [subject, start, end, required, optional, locations] = await Promise.all([
      this.getAsync<string>(item.subject),
      this.getAsync<Date>(item.start),
      this.getAsync<Date>(item.end),
      this.getAsync<any[]>(item.requiredAttendees),
      this.getAsync<any[]>(item.optionalAttendees),
      this.getEnhancedLocations(),
    ]);
    return {
      subject: subject ?? '',
      start: start ?? new Date(),
      end: end ?? new Date(Date.now() + 30 * 60000),
      roomEmails: locations,
      requiredAttendees: (required ?? []).map(mapRecipient),
      optionalAttendees: (optional ?? []).map(mapRecipient),
      organizer: null,
      iCalUId: null,
    };
  }

  override async setAppointmentTimes(start: Date, end: Date): Promise<void> {
    await this.setAsync(this.item?.start, start);
    await this.setAsync(this.item?.end, end);
  }

  override async addRoomLocation(roomEmail: string, displayName: string): Promise<void> {
    const enhanced = this.item?.enhancedLocation;
    if (!enhanced?.addAsync) throw new Error('enhancedLocation unavailable (requires Mailbox 1.8)');
    const type = this.office?.MailboxEnums?.LocationType?.Room ?? 'room';
    await new Promise<void>((resolve, reject) => {
      enhanced.addAsync([{ id: roomEmail, type }], (res: any) =>
        res.status === 'succeeded' ? resolve() : reject(new Error(res.error?.message ?? `Could not add ${displayName}`)),
      );
    });
  }

  override async removeRoomLocation(roomEmail: string): Promise<void> {
    const enhanced = this.item?.enhancedLocation;
    if (!enhanced?.removeAsync) throw new Error('enhancedLocation unavailable (requires Mailbox 1.8)');
    const type = this.office?.MailboxEnums?.LocationType?.Room ?? 'room';
    await new Promise<void>((resolve, reject) => {
      enhanced.removeAsync([{ id: roomEmail, type }], (res: any) =>
        res.status === 'succeeded' ? resolve() : reject(new Error(res.error?.message ?? 'Could not remove the room')),
      );
    });
  }

  override on<E extends keyof OfficeAdapterEvents>(event: E, handler: OfficeAdapterEvents[E]): () => void {
    const eventTypes = this.office?.EventType;
    const map: Record<keyof OfficeAdapterEvents, { target: any; type: any }> = {
      timeChanged: { target: this.item, type: eventTypes?.AppointmentTimeChanged },
      locationsChanged: { target: this.item, type: eventTypes?.EnhancedLocationsChanged },
      recipientsChanged: { target: this.item, type: eventTypes?.RecipientsChanged },
      themeChanged: { target: this.mailbox, type: eventTypes?.OfficeThemeChanged },
    };
    const entry = map[event];
    if (!entry?.target?.addHandlerAsync || entry.type === undefined) return () => undefined;

    const wrapped = () => {
      if (event === 'themeChanged') {
        (handler as OfficeAdapterEvents['themeChanged'])(this.officeTheme() ?? 'light');
        return;
      }
      void this.readAppointment().then((snap) => {
        if (snap) (handler as OfficeAdapterEvents['timeChanged'])(snap);
      });
    };
    entry.target.addHandlerAsync(entry.type, wrapped);
    return () => entry.target.removeHandlerAsync?.(entry.type, { handler: wrapped });
  }

  override officeTheme(): OfficeThemeMode | null {
    const theme = this.office?.context?.officeTheme;
    if (!theme?.bodyBackgroundColor) return null;
    // Perceived luminance of the host body background decides dark/light.
    const hex = String(theme.bodyBackgroundColor).replace('#', '');
    if (hex.length < 6) return null;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128 ? 'dark' : 'light';
  }

  override initializationContext(): Promise<PaneInitializationContext | null> {
    const item = this.item;
    if (!item?.getInitializationContextAsync) return Promise.resolve(null);
    return new Promise((resolve) => {
      item.getInitializationContextAsync((res: any) => {
        if (res.status !== 'succeeded' || !res.value) return resolve(null);
        try {
          resolve(JSON.parse(res.value) as PaneInitializationContext);
        } catch {
          resolve(null);
        }
      });
    });
  }

  override async openItemForEditing(): Promise<void> {
    // On read surfaces Outlook exposes the native edit affordance; the pane
    // can only prompt. displayAppointmentForm reopens by id where available.
    const id = this.item?.itemId;
    if (id && this.mailbox?.displayAppointmentFormAsync) {
      this.mailbox.displayAppointmentFormAsync(id);
    } else if (id && this.mailbox?.displayAppointmentForm) {
      this.mailbox.displayAppointmentForm(id);
    }
  }

  override showInsightMessage(key: string, message: string): Promise<void> {
    const notifications = this.item?.notificationMessages;
    if (!notifications?.addAsync) return Promise.resolve();
    return new Promise((resolve) => {
      notifications.addAsync(
        key,
        {
          type: 'insightMessage',
          message,
          icon: 'icon16',
          actions: [{ actionType: 'showTaskPane', actionText: 'Open RICOH Workplace', commandId: 'msgComposeOpenPaneButton' }],
        },
        () => resolve(),
      );
    });
  }

  private async getEnhancedLocations(): Promise<string[]> {
    const enhanced = this.item?.enhancedLocation;
    if (!enhanced?.getAsync) return [];
    return new Promise((resolve) => {
      enhanced.getAsync((res: any) => {
        if (res.status !== 'succeeded') return resolve([]);
        const rooms = (res.value ?? [])
          .filter((l: any) => (l.locationIdentifier?.type ?? l.type) === 'room')
          .map((l: any) => l.locationIdentifier?.id ?? l.id)
          .filter(Boolean);
        resolve(rooms);
      });
    });
  }

  private getAsync<T>(target: any): Promise<T | null> {
    if (!target?.getAsync) return Promise.resolve(typeof target === 'object' ? null : (target ?? null));
    return new Promise((resolve) => {
      target.getAsync((res: any) => resolve(res.status === 'succeeded' ? (res.value as T) : null));
    });
  }

  private setAsync(target: any, value: unknown): Promise<void> {
    if (!target?.setAsync) return Promise.resolve();
    return new Promise((resolve, reject) => {
      target.setAsync(value, (res: any) =>
        res.status === 'succeeded' ? resolve() : reject(new Error(res.error?.message ?? 'setAsync failed')),
      );
    });
  }
}

function mapRecipient(r: any): { name: string; email: string } {
  return { name: r.displayName ?? r.name ?? r.emailAddress ?? '', email: r.emailAddress ?? r.address ?? '' };
}
